/**
 * SkinReportCard — Shareable visual Skin Report Card
 *
 * Generates a branded card from the user's AI skin analysis data.
 * Users can download it as a PNG or share via the Web Share API.
 */
import { useRef, useState, useCallback, useEffect } from 'react';
import html2canvas from 'html2canvas';
import type { SkinSection } from '../../types/profile';
import { useTranslation } from 'react-i18next';
import './SkinReportCard.css';

interface SkinReportCardProps {
  skin: SkinSection;
  userName?: string;
  onClose: () => void;
}

const SKIN_TYPE_EMOJI: Record<string, string> = {
  dry: '🏜️',
  oily: '💧',
  combination: '🔀',
  sensitive: '🌸',
  normal: '✨',
};

export default function SkinReportCard({ skin, userName, onClose }: SkinReportCardProps) {
  const { t } = useTranslation();
  const cardRef = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false);

  const skinType = skin.type ?? skin.aiAnalysis?.skinType ?? 'unknown';
  const concerns = skin.concerns ?? skin.aiAnalysis?.concerns ?? [];
  const recommendations = skin.aiAnalysis?.recommendations ?? [];
  const confidence = skin.aiAnalysis?.confidence;
  const analysisDate = skin.aiAnalysis?.analysisDate
    ? new Date(skin.aiAnalysis.analysisDate).toLocaleDateString()
    : null;

  const captureCard = useCallback(async (): Promise<Blob | null> => {
    if (!cardRef.current) return null;
    const canvas = await html2canvas(cardRef.current, {
      backgroundColor: null,
      scale: 2,
      useCORS: true,
    });
    return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
  }, []);

  const handleDownload = useCallback(async () => {
    setBusy(true);
    try {
      const blob = await captureCard();
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'heseda-skin-report.png';
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setBusy(false);
    }
  }, [captureCard]);

  const handleShare = useCallback(async () => {
    setBusy(true);
    try {
      const blob = await captureCard();
      if (!blob) return;
      const file = new File([blob], 'heseda-skin-report.png', { type: 'image/png' });
      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          title: 'My Skin Report — Heseda AI',
          text: 'Check out my AI skin analysis from Heseda!',
          files: [file],
        });
      } else if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
        // Fallback: copy image to clipboard
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        alert(t('copiedToClipboard', 'Copied to clipboard!'));
      } else {
        // Final fallback: download the image instead
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'heseda-skin-report.png';
        a.click();
        URL.revokeObjectURL(url);
        alert(t('downloadedFallback', 'Image downloaded (sharing not supported in this browser)'));
      }
    } finally {
      setBusy(false);
    }
  }, [captureCard, t]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const confidencePct = confidence != null ? Math.round(Number(confidence) * 100) : null;

  return (
    <div className="report-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label="Skin Report Card">
      <div className="report-wrapper" onClick={e => e.stopPropagation()}>
        {/* The card that gets captured as an image */}
        <div ref={cardRef} className="report-card">
          <div className="report-card__header">
            <div className="report-card__logo">✦ Heseda AI</div>
            <div className="report-card__subtitle">Skin Report Card</div>
          </div>

          {userName && <div className="report-card__name">{userName}</div>}

          <div className="report-card__type">
            <span className="report-card__type-emoji">
              {SKIN_TYPE_EMOJI[skinType] ?? '🔬'}
            </span>
            <span className="report-card__type-label">{skinType}</span>
          </div>

          {concerns.length > 0 && (
            <div className="report-card__section">
              <h4 className="report-card__section-title">Top Concerns</h4>
              <div className="report-card__tags">
                {concerns.slice(0, 5).map(c => (
                  <span key={c} className="report-card__tag">{c}</span>
                ))}
              </div>
            </div>
          )}

          {recommendations.length > 0 && (
            <div className="report-card__section">
              <h4 className="report-card__section-title">Recommendations</h4>
              <ul className="report-card__recs">
                {recommendations.slice(0, 3).map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="report-card__footer">
            {confidencePct != null && (
              <span className="report-card__confidence">
                {confidencePct}% confidence
              </span>
            )}
            {analysisDate && (
              <span className="report-card__date">{analysisDate}</span>
            )}
          </div>

          <div className="report-card__watermark">heseda.ai</div>
        </div>

        {/* Action buttons (outside the captured area) */}
        <div className="report-actions">
          <button
            type="button"
            className="report-actions__btn report-actions__btn--download"
            onClick={handleDownload}
            disabled={busy}
          >
            {busy ? '...' : '⬇ Download'}
          </button>
          <button
            type="button"
            className="report-actions__btn report-actions__btn--share"
            onClick={handleShare}
            disabled={busy}
          >
            {busy ? '...' : '🔗 Share'}
          </button>
          <button
            type="button"
            className="report-actions__btn report-actions__btn--close"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}
